import React from 'react';
import { ShieldCheck } from 'lucide-react';

const SECTIONS = [
  {
    title: '1. Introduction',
    body: (
      <>
        <p>
          Welcome to Tiffsy ("we", "our", or "us"). We operate the Tiffsy mobile application and
          related services that enable customers to subscribe to and receive home-style meal
          deliveries.
        </p>
        <p>
          This Privacy Policy explains how we collect, use, disclose, and safeguard your
          information when you use our app. Please read it carefully. By using Tiffsy you agree
          to the practices described here.
        </p>
      </>
    ),
  },
  {
    title: '2. Information We Collect',
    body: (
      <>
        <p className="font-medium text-gray-700">Information you provide directly:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Name, phone number, and email address (used for account creation and authentication via OTP)</li>
          <li>Delivery addresses and location data</li>
          <li>Meal preferences and subscription details</li>
          <li>Payment information (processed securely through Razorpay; we do not store card details)</li>
          <li>Profile photo (optional)</li>
        </ul>
        <p className="font-medium text-gray-700 mt-4">Information collected automatically:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Device identifiers and push-notification tokens (FCM)</li>
          <li>App usage data and crash reports</li>
          <li>Log data (IP address, timestamps, pages visited)</li>
        </ul>
      </>
    ),
  },
  {
    title: '3. How We Use Your Information',
    body: (
      <ul className="list-disc list-inside space-y-1">
        <li>To create and manage your account</li>
        <li>To process and deliver your meal subscriptions and orders</li>
        <li>To facilitate payments and issue refunds</li>
        <li>To send order updates, delivery notifications, and service-related communications</li>
        <li>To administer referral and voucher programs</li>
        <li>To improve our app, services, and customer support</li>
        <li>To comply with legal obligations</li>
      </ul>
    ),
  },
  {
    title: '4. Sharing Your Information',
    body: (
      <>
        <p>We do not sell your personal data. We may share it only in the following circumstances:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><span className="font-medium text-gray-700">Delivery partners / drivers</span> – name and delivery address to fulfil your order</li>
          <li><span className="font-medium text-gray-700">Kitchen partners</span> – order details necessary to prepare your meals</li>
          <li><span className="font-medium text-gray-700">Payment processors</span> – Razorpay for secure payment handling</li>
          <li><span className="font-medium text-gray-700">Cloud service providers</span> – AWS, Google Firebase, and Cloudinary for infrastructure and storage</li>
          <li><span className="font-medium text-gray-700">Legal requirements</span> – when required by law, court order, or government authority</li>
        </ul>
      </>
    ),
  },
  {
    title: '5. Data Retention',
    body: (
      <p>
        We retain your personal data for as long as your account is active or as needed to
        provide our services. You may request deletion of your account and associated data at
        any time by contacting us at{' '}
        <a href="mailto:support@tiffsy.in" className="text-orange-600 hover:underline">
          support@tiffsy.in
        </a>
        .
      </p>
    ),
  },
  {
    title: '6. Data Security',
    body: (
      <p>
        We implement industry-standard security measures including HTTPS encryption, secure
        token authentication, and access controls. However, no method of transmission over the
        internet is 100% secure, and we cannot guarantee absolute security.
      </p>
    ),
  },
  {
    title: "7. Children's Privacy",
    body: (
      <p>
        Tiffsy is not directed to children under the age of 13. We do not knowingly collect
        personal information from children. If you believe a child has provided us with personal
        information, please contact us and we will delete it.
      </p>
    ),
  },
  {
    title: '8. Your Rights',
    body: (
      <>
        <p>Depending on your jurisdiction, you may have the right to:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Access the personal data we hold about you</li>
          <li>Correct inaccurate data</li>
          <li>Request deletion of your data</li>
          <li>Withdraw consent for marketing communications</li>
        </ul>
        <p className="mt-2">
          To exercise any of these rights, contact us at{' '}
          <a href="mailto:support@tiffsy.in" className="text-orange-600 hover:underline">
            support@tiffsy.in
          </a>
          .
        </p>
      </>
    ),
  },
  {
    title: '9. Third-Party Links',
    body: (
      <p>
        Our app may contain links to third-party websites or services. We are not responsible
        for the privacy practices of those third parties and encourage you to review their
        privacy policies.
      </p>
    ),
  },
  {
    title: '10. Changes to This Policy',
    body: (
      <p>
        We may update this Privacy Policy from time to time. We will notify you of significant
        changes via the app or email. The "Last updated" date at the top of this page reflects
        the most recent revision.
      </p>
    ),
  },
  {
    title: '11. Contact Us',
    body: (
      <p>
        If you have any questions about this Privacy Policy, please reach out:
        <br />
        Email:{' '}
        <a href="mailto:support@tiffsy.in" className="text-orange-600 hover:underline">
          support@tiffsy.in
        </a>
        <br />
        Website:{' '}
        <a href="https://tiffsy.in/support" className="text-orange-600 hover:underline">
          tiffsy.in/support
        </a>
      </p>
    ),
  },
];

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-orange-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center space-x-3">
          <a href="/" className="flex items-center space-x-3">
            <img
              src="https://customer-assets.emergentagent.com/job_cf5884b0-6cc6-42b4-b863-35f98b8ffb31/artifacts/rn9a0bc7_WhatsApp%20Image%202026-02-11%20at%2017.09.43.jpeg"
              alt="Tiffsy Logo"
              className="h-12 w-12 object-contain rounded-lg"
            />
            <span className="text-2xl font-bold text-orange-600">Tiffsy</span>
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-16 max-w-3xl">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <ShieldCheck className="w-8 h-8 text-orange-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-500">Last updated: March 14, 2026</p>
        </div>

        <div className="space-y-6">
          {SECTIONS.map((section) => (
            <div
              key={section.title}
              className="bg-white rounded-2xl shadow-sm border border-orange-100 p-6"
            >
              <h2 className="text-xl font-semibold text-gray-800 mb-4">{section.title}</h2>
              <div className="text-gray-600 space-y-2 leading-relaxed">{section.body}</div>
            </div>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="text-center py-8 text-gray-400 text-sm">
        © {new Date().getFullYear()} Tiffsy. All rights reserved.
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
